// The ED Referral Letter (KMC request, 2026-10-28) — Emergency / Casualty →
// Documents → Referral letter.
//
// Left: the form (template, date, receiving doctor, diagnosis, reason,
// recommended management, referring doctor). Right: the letter as it prints,
// in the Kongu Medical Center format, with its text editable in place.
//
// The text starts as a draft built from the ED record and the form
// (referralTemplates.ts) and keeps following the form until the doctor edits
// it; after that, "Rebuild text from the form" replaces it on request.
// Save draft / Preview / Print / Download PDF save first, so the PDF is always
// the letter on screen. Finalizing (er:documents) files the PDF in Patient
// Records and locks the letter; a change after that is an amendment — a new
// version with a reason, the old one kept and marked superseded.
//
// "✨ Write with AI" (when the hospital has AI on) asks the ED assistant for
// the body only. The server builds the ED record and adds the hospital's
// active "Referral Letter" AI Assistant Role profile (Admin → AI Assistant
// Role) to the prompt; the reply replaces the text, with Undo.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Eye, FileCheck2, PenLine, Printer, RotateCcw, Save, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/Dialog';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/hooks/useBranding';
import { toast } from '@/hooks/useToast';
import { ToastAction } from '@/components/ui/Toast';
import { ApiError } from '@/lib/api/client';
import { getAiSettings } from '@/api';
import { loadConfig, getActiveModel } from '@/aiConfig';
import { generateDocument, GenerationError } from '@/features/ai-assist/streamGeneration';
import { cleanEdDoc, parseEdReply } from '../documents/edDocs';
import { cn } from '@/lib/cn';
import { keys } from '@/lib/queryKeys';
import type { Workspace } from '../types';
import {
  amendReferralLetter, createReferralLetter, discardReferralLetter, fetchReferralLetter, fetchReferralPdf,
  fetchReferralPrefill, finalizeReferralLetter, saveReferralLetter,
  type LetterInput, type Recipient, type ReferralLetter, type ReferralPrefill, type Signature,
} from './api';
import {
  REFERRAL_TEMPLATES, buildAiLetterRequest, composeReferralBody, drName, fmtLetterDate, letterBodyFromAiReply,
  missingForFinal, type TemplateKey,
} from './referralTemplates';

interface Form {
  template_key: TemplateKey;
  letter_date: string;
  recipient: Recipient;
  salutation: string;
  diagnosis: string;
  referral_reason: string;
  recommended_management: string;
  body: string;
  signature: Signature;
}

const toInput = (f: Form): LetterInput => ({
  template_key: f.template_key,
  letter_date: f.letter_date,
  recipient: f.recipient,
  salutation: f.salutation,
  diagnosis: f.diagnosis || null,
  referral_reason: f.referral_reason || null,
  recommended_management: f.recommended_management || null,
  body: f.body,
  signature: f.signature,
});

/** Stable comparison of what would be saved (trims, drops empty keys). */
function snapshot(f: Form): string {
  const obj = (o: Record<string, string | undefined>) =>
    Object.fromEntries(Object.entries(o).map(([k, v]) => [k, (v ?? '').trim()]).filter(([, v]) => v));
  return JSON.stringify({
    ...toInput(f),
    recipient: obj(f.recipient as Record<string, string | undefined>),
    signature: obj(f.signature as Record<string, string | undefined>),
    salutation: f.salutation.trim(),
    diagnosis: f.diagnosis.trim(), referral_reason: f.referral_reason.trim(),
    recommended_management: f.recommended_management.trim(), body: f.body.trim(),
  });
}

function fromLetter(l: ReferralLetter): Form {
  return {
    template_key: l.template_key, letter_date: l.letter_date, recipient: { ...(l.recipient || {}) },
    salutation: l.salutation, diagnosis: l.diagnosis ?? '', referral_reason: l.referral_reason ?? '',
    recommended_management: l.recommended_management ?? '', body: l.body ?? '', signature: { ...(l.signature || {}) },
  };
}

function printBlobUrl(url: string) {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.src = url;
  frame.onload = () => {
    try { frame.contentWindow?.focus(); frame.contentWindow?.print(); }
    catch { window.open(url, '_blank', 'noopener'); }
    setTimeout(() => frame.remove(), 60000);
  };
  document.body.appendChild(frame);
}

const STATUS_BADGE: Record<ReferralLetter['status'], { label: string; variant: 'warn' | 'success' | 'neutral' }> = {
  draft: { label: 'Draft', variant: 'warn' },
  final: { label: 'Final', variant: 'success' },
  superseded: { label: 'Superseded', variant: 'neutral' },
};

export default function ReferralLetterComposer({ ws, letterId, onClose, onOpenLetter }: {
  ws: Workspace;
  /** null = a new letter (nothing is saved until the doctor saves or previews). */
  letterId: number | null;
  onClose: () => void;
  onOpenLetter: (id: number) => void;
}) {
  const enc = ws.encounter;
  const { user, branchVersion } = useAuth();
  const { branding } = useBranding();
  const qc = useQueryClient();
  const modules = user?.modules ?? [];
  const canDraft = modules.includes('er:clinical');
  const canFinalize = modules.includes('er:documents');

  const prefillQuery = useQuery({
    queryKey: [...keys.emergency.encounter(enc.id), 'referral-letters', 'prefill', branchVersion],
    queryFn: () => fetchReferralPrefill(enc.id),
    staleTime: 0,
  });
  const letterQuery = useQuery({
    queryKey: [...keys.emergency.encounter(enc.id), 'referral-letters', 'one', letterId, branchVersion],
    queryFn: () => fetchReferralLetter(letterId as number),
    enabled: letterId != null,
    staleTime: 0,
  });

  const [letter, setLetter] = useState<ReferralLetter | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [saved, setSaved] = useState<string>('');
  const [busy, setBusy] = useState<null | 'save' | 'preview' | 'print' | 'download' | 'finalize' | 'discard' | 'amend' | 'ai'>(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  // What the last "Write with AI" followed: a profile name, or null = the built-in instructions.
  const [aiProfile, setAiProfile] = useState<string | null | undefined>(undefined);
  const [aiCfg] = useState(loadConfig);
  const aiAbort = useRef<AbortController | null>(null);
  useEffect(() => {
    getAiSettings().then((st: { ai_processing_enabled?: boolean }) => setAiEnabled(!!st.ai_processing_enabled)).catch(() => setAiEnabled(false));
    return () => aiAbort.current?.abort();
  }, []);
  const [problems, setProblems] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | 'finalize' | 'discard' | 'rebuild' | 'leave'>(null);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendError, setAmendError] = useState<string | null>(null);
  const initFor = useRef<string | null>(null);

  const prefill = prefillQuery.data;
  const compose = (f: Form, p: ReferralPrefill | undefined = prefill) => (p
    ? composeReferralBody(p.patient, p.facts, {
      template_key: f.template_key, diagnosis: f.diagnosis, referral_reason: f.referral_reason,
      recommended_management: f.recommended_management,
    })
    : f.body);

  // Build the form once per letter, when its data has arrived.
  useEffect(() => {
    const key = String(letterId ?? 'new');
    if (initFor.current === key || !prefill) return;
    if (letterId != null && !letterQuery.data) return;
    let f: Form;
    if (letterId != null && letterQuery.data) {
      setLetter(letterQuery.data);
      f = fromLetter(letterQuery.data);
      setSaved(snapshot(f));
    } else {
      const d = prefill.defaults;
      f = {
        template_key: d.template_key, letter_date: d.letter_date, recipient: { ...d.recipient },
        salutation: d.salutation, diagnosis: d.diagnosis ?? '', referral_reason: d.referral_reason ?? '',
        recommended_management: d.recommended_management ?? '', body: '', signature: { ...d.signature },
      };
      f.body = compose(f, prefill);
      setLetter(null);
      setSaved('');
    }
    setForm(f);
    setProblems([]);
    initFor.current = key;
  }, [letterId, prefill, letterQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const editable = canDraft && (!letter || letter.status === 'draft');
  const dirty = form ? snapshot(form) !== saved : false;
  const bodyFollowsForm = form ? form.body === compose(form) : true;

  /** Applies a change; the text keeps following the form until it is hand-edited. */
  const update = (patch: (f: Form) => Partial<Form>) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch(prev) };
      if (prev.body === compose(prev)) next.body = compose(next);
      return next;
    });
    setProblems([]);
  };
  const setRecipient = (k: keyof Recipient, v: string) => update((f) => ({ recipient: { ...f.recipient, [k]: v } }));
  const setSignature = (k: keyof Signature, v: string) => update((f) => ({ signature: { ...f.signature, [k]: v } }));

  const invalidateList = () => qc.invalidateQueries({ queryKey: [...keys.emergency.encounter(enc.id), 'referral-letters'] });

  const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

  /** Saves the draft if needed and returns the letter as stored. */
  const save = async (): Promise<ReferralLetter> => {
    if (!form) throw new Error('The letter is still loading');
    if (letter && (!editable || !dirty)) return letter;
    const input = toInput(form);
    try {
      const row = letter
        ? await saveReferralLetter(letter.id, { ...input, expected_revision: letter.revision })
        : await createReferralLetter(enc.id, input);
      setLetter(row);
      setSaved(snapshot(form));
      invalidateList();
      if (!letter) {
        // The parent now points at this letter's id; the form already holds
        // it, so don't rebuild the form from a fetch that could land after a
        // finalize and show the letter as a draft again.
        initFor.current = String(row.id);
        onOpenLetter(row.id);
      }
      return row;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'conflict-stale') {
        throw new Error('Someone else saved this letter after you opened it. Go back and open it again to see their changes.');
      }
      throw e;
    }
  };

  const run = async (kind: NonNullable<typeof busy>, fn: () => Promise<void>) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast({ variant: 'danger', title: 'Could not complete that', description: errorText(e) }); } finally { setBusy(null); }
  };

  const onSave = () => run('save', async () => { await save(); toast({ variant: 'success', title: 'Draft saved' }); });

  const pdfName = () => `${(prefill?.patient.name || 'Patient').replace(/[^A-Za-z0-9]+/g, '_')}_Referral_Letter${letter?.status === 'draft' || !letter ? '_DRAFT' : ''}.pdf`;

  const withPdf = (mode: 'preview' | 'print' | 'download') => run(mode, async () => {
    const l = editable ? await save() : letter;
    if (!l) return;
    const url = URL.createObjectURL(await fetchReferralPdf(l.id));
    if (mode === 'preview') { setPreviewUrl(url); return; }
    if (mode === 'print') { printBlobUrl(url); return; }
    const a = document.createElement('a');
    a.href = url;
    a.download = pdfName();
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  });

  const onFinalize = () => run('finalize', async () => {
    const l = await save();
    try {
      const fin = await finalizeReferralLetter(l.id, l.revision);
      setLetter(fin);
      invalidateList();
      qc.invalidateQueries({ queryKey: [...keys.emergency.encounter(enc.id), 'documents'] });
      toast({ variant: 'success', title: 'Referral letter finalized', description: 'The PDF is filed in Patient Records → Documents.' });
    } catch (e) {
      const list = e instanceof ApiError ? (e.details?.problems as Array<{ message: string }> | undefined) : undefined;
      if (list?.length) { setProblems(list.map((p) => p.message)); return; }
      throw e;
    }
  });

  const onDiscard = () => run('discard', async () => {
    if (letter) await discardReferralLetter(letter.id);
    invalidateList();
    onClose();
  });

  const onAmend = async (reason: string) => {
    if (!letter) return;
    setBusy('amend'); setAmendError(null);
    try {
      const draft = await amendReferralLetter(letter.id, reason);
      setAmendOpen(false);
      invalidateList();
      onOpenLetter(draft.id);
    } catch (e) {
      setAmendError(errorText(e));
    } finally { setBusy(null); }
  };

  const writeWithAi = async () => {
    if (!form || !prefill || busy) return;
    const before = form.body;
    const controller = new AbortController();
    aiAbort.current = controller;
    setBusy('ai');
    try {
      const pt = prefill.patient;
      const result = await generateDocument({
        model: getActiveModel(aiCfg),
        patient: { name: pt.name, uhid: pt.uhid, gender: pt.gender ?? '', age: pt.age && !pt.age.approximate ? pt.age.years : null, approx_age: pt.age?.approximate ? pt.age.years : null },
        messages: [{ role: 'user', content: buildAiLetterRequest(form) }],
        doc_type: 'Referral Letter',
        temperature: aiCfg.temperature,
        max_tokens: Math.max(aiCfg.maxTokens ?? 0, 1500),
        er_encounter_id: enc.id,
      }, { signal: controller.signal });
      const parsed = parseEdReply(result.content);
      const body = letterBodyFromAiReply(cleanEdDoc(parsed.docText ?? result.content));
      if (!body) throw new Error("The AI's reply had no letter text — nothing changed.");
      setForm((f) => (f ? { ...f, body } : f));
      setProblems([]);
      setAiProfile(result.applied_profile?.name ?? null);
      toast({
        variant: 'success', title: 'Letter text written by AI', duration: 10000,
        description: 'Check every line against the record before finalizing.',
        action: <ToastAction altText="Undo" onClick={() => setForm((f) => (f ? { ...f, body: before } : f))}>Undo</ToastAction>,
      });
    } catch (e) {
      if ((e as { name?: string } | null)?.name === 'AbortError') return;
      const msg = e instanceof GenerationError && e.code === 'AI_CONSENT_REQUIRED'
        ? 'This patient has no AI-processing consent on file. Record it in Patient Records, or write the letter by hand.'
        : errorText(e);
      toast({ variant: 'danger', title: 'Could not write the letter with AI', description: msg });
    } finally {
      aiAbort.current = null;
      setBusy(null);
    }
  };

  const missing = useMemo(() => (form ? missingForFinal(form) : []), [form]);

  if (prefillQuery.isError || letterQuery.isError) {
    return (
      <div className="flex flex-col gap-2">
        <Button size="sm" variant="ghost" className="self-start" onClick={onClose}><ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back to documents</Button>
        <div className="rounded-[var(--radius)] border border-danger/30 bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">
          ⚠ Could not load the referral letter: {errorText(prefillQuery.error ?? letterQuery.error)}
        </div>
      </div>
    );
  }
  if (!form || !prefill) {
    return <div className="p-6 text-center text-[12.5px] text-ink-muted">Loading the referral letter…</div>;
  }

  const p = prefill.patient;
  const lh = prefill.letterhead;
  const r = form.recipient;
  const s = form.signature;
  const status = letter?.status ?? 'draft';
  const tpl = REFERRAL_TEMPLATES.find((t) => t.key === form.template_key);
  const ro = !editable;
  const placeholder = (text: string) => <span className="italic text-neutral-400">{text}</span>;

  return (
    <div className="flex flex-col gap-2">
      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-line bg-card px-3 py-2">
        <Button size="sm" variant="ghost" onClick={() => (dirty && editable ? setConfirm('leave') : onClose())}>
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back to documents
        </Button>
        <span className="text-[13px] font-semibold text-ink">Referral letter</span>
        <Badge variant={STATUS_BADGE[status].variant}>{letter ? STATUS_BADGE[status].label : 'New — not saved'}</Badge>
        {letter && letter.version > 1 && <Badge variant="info">Version {letter.version}</Badge>}
        {dirty && editable && letter && <span className="text-[11.5px] text-warn-ink">Unsaved changes</span>}
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {editable && (
            <Button size="sm" variant="ghost" loading={busy === 'save'} disabled={!!busy || (!!letter && !dirty)} onClick={onSave}>
              <Save className="h-3.5 w-3.5" aria-hidden="true" /> Save draft
            </Button>
          )}
          <Button size="sm" variant="ghost" loading={busy === 'preview'} disabled={!!busy || (!letter && !editable)} onClick={() => withPdf('preview')}>
            <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Preview
          </Button>
          <Button size="sm" variant="ghost" loading={busy === 'print'} disabled={!!busy || (!letter && !editable)} onClick={() => withPdf('print')}>
            <Printer className="h-3.5 w-3.5" aria-hidden="true" /> Print
          </Button>
          <Button size="sm" variant="ghost" loading={busy === 'download'} disabled={!!busy || (!letter && !editable)} onClick={() => withPdf('download')}>
            <Download className="h-3.5 w-3.5" aria-hidden="true" /> Download PDF
          </Button>
          {editable && letter && (
            <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setConfirm('discard')} title="Discard this draft">
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Discard
            </Button>
          )}
          {editable && (
            <Button size="sm" variant="success" loading={busy === 'finalize'} disabled={!!busy || !canFinalize || missing.length > 0}
              title={!canFinalize ? 'Finalizing a referral letter needs the er:documents permission (doctors by default)' : missing.length ? `Add ${missing.join(', ')}` : undefined}
              onClick={() => setConfirm('finalize')}>
              <FileCheck2 className="h-3.5 w-3.5" aria-hidden="true" /> Finalize
            </Button>
          )}
          {letter?.status === 'final' && canFinalize && (
            <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => { setAmendError(null); setAmendOpen(true); }}>
              <PenLine className="h-3.5 w-3.5" aria-hidden="true" /> Amend
            </Button>
          )}
        </div>
      </div>

      {letter?.amendment_reason && (
        <div className="rounded-[var(--radius)] border border-line bg-muted px-3 py-2 text-[12px] text-ink-muted">
          Amendment of version {letter.version - 1}. Reason: <b className="text-ink">{letter.amendment_reason}</b>
        </div>
      )}
      {letter?.status === 'final' && (
        <div className="rounded-[var(--radius)] border border-success/30 bg-success-soft px-3 py-2 text-[12px] text-success-ink">
          Finalized{letter.finalized_by ? ` by ${letter.finalized_by}` : ''}{letter.finalized_at ? ` on ${new Date(letter.finalized_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}. Filed in Patient Records → Documents. It can't be edited; use Amend to issue a corrected version.
        </div>
      )}
      {letter?.status === 'superseded' && (
        <div className="rounded-[var(--radius)] border border-line bg-muted px-3 py-2 text-[12px] text-ink-muted">
          This version was replaced by an amended letter. It is kept as it was sent.
        </div>
      )}
      {editable && !canFinalize && (
        <div className="rounded-[var(--radius)] border border-line bg-muted px-3 py-2 text-[12px] text-ink-muted">
          You can prepare and save this letter. A doctor needs to finalize it.
        </div>
      )}
      {problems.length > 0 && (
        <div className="rounded-[var(--radius)] border border-danger/30 bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">
          ⚠ Complete the letter first: {problems.join('; ')}
        </div>
      )}

      <div className="grid gap-3 xl:grid-cols-[minmax(320px,400px)_1fr]">
        {/* Form */}
        <fieldset disabled={ro} className="flex min-w-0 flex-col gap-3 rounded-[var(--radius)] border border-line bg-card p-3">
          <div className="text-[12px] font-semibold text-ink-muted">Letter</div>
          <FormField label="Template" htmlFor="rl-template" hint={tpl?.description}>
            <NativeSelect id="rl-template" value={form.template_key} onChange={(e) => update(() => ({ template_key: e.target.value as TemplateKey }))}>
              {REFERRAL_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </NativeSelect>
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Date" htmlFor="rl-date">
              <Input id="rl-date" type="date" value={form.letter_date} onChange={(e) => update(() => ({ letter_date: e.target.value }))} />
            </FormField>
            <FormField label="Salutation" htmlFor="rl-sal">
              <Input id="rl-sal" value={form.salutation} maxLength={60} onChange={(e) => update(() => ({ salutation: e.target.value }))} />
            </FormField>
          </div>

          <div className="mt-1 text-[12px] font-semibold text-ink-muted">To — receiving doctor</div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Doctor's name" htmlFor="rl-rd">
              <Input id="rl-rd" value={r.doctor_name ?? ''} maxLength={120} placeholder="e.g. Ravi Kumar" onChange={(e) => setRecipient('doctor_name', e.target.value)} />
            </FormField>
            <FormField label="Qualification" htmlFor="rl-rq">
              <Input id="rl-rq" value={r.qualification ?? ''} maxLength={120} placeholder="e.g. MD, DM (Cardiology)" onChange={(e) => setRecipient('qualification', e.target.value)} />
            </FormField>
          </div>
          <FormField label="Designation / specialty" htmlFor="rl-rs">
            <Input id="rl-rs" value={r.designation ?? ''} maxLength={150} placeholder="e.g. Consultant Cardiologist" onChange={(e) => setRecipient('designation', e.target.value)} />
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Hospital" htmlFor="rl-rh" hint={prefill.facts.referral?.destination ? 'From the referral disposition' : undefined}>
              <Input id="rl-rh" value={r.hospital ?? ''} maxLength={200} onChange={(e) => setRecipient('hospital', e.target.value)} />
            </FormField>
            <FormField label="City" htmlFor="rl-rc">
              <Input id="rl-rc" value={r.city ?? ''} maxLength={100} onChange={(e) => setRecipient('city', e.target.value)} />
            </FormField>
          </div>

          <div className="mt-1 text-[12px] font-semibold text-ink-muted">Clinical</div>
          <FormField label="Provisional / confirmed diagnosis" htmlFor="rl-dx"
            hint={prefill.facts.diagnosis ? 'From the ED record — check it' : 'No diagnosis is recorded in the ED notes — type one, or leave blank to describe the complaint instead.'}>
            <Input id="rl-dx" value={form.diagnosis} maxLength={500} onChange={(e) => update(() => ({ diagnosis: e.target.value }))} />
          </FormField>
          <FormField label="Reason for referral" htmlFor="rl-reason" required>
            <textarea id="rl-reason" rows={3} maxLength={2000} value={form.referral_reason}
              className="w-full rounded-[var(--radius)] border border-border bg-card px-2.5 py-1.5 text-[13px] text-ink"
              placeholder="e.g. Needs coronary angiography; cath lab not available here"
              onChange={(e) => update(() => ({ referral_reason: e.target.value }))} />
          </FormField>
          <FormField label="Recommended evaluation / treatment" htmlFor="rl-rec" hint='Completes "Kindly assess the patient for further management, including …"'>
            <Input id="rl-rec" value={form.recommended_management} maxLength={1000} placeholder="e.g. coronary angiography and revascularisation"
              onChange={(e) => update(() => ({ recommended_management: e.target.value }))} />
          </FormField>

          <div className="mt-1 text-[12px] font-semibold text-ink-muted">Referring doctor (signature)</div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Name" htmlFor="rl-sn" required>
              <Input id="rl-sn" value={s.doctor_name ?? ''} maxLength={120} onChange={(e) => setSignature('doctor_name', e.target.value)} />
            </FormField>
            <FormField label="Qualification" htmlFor="rl-sq">
              <Input id="rl-sq" value={s.qualification ?? ''} maxLength={120} placeholder="e.g. MBBS, MD" onChange={(e) => setSignature('qualification', e.target.value)} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Designation" htmlFor="rl-sd">
              <Input id="rl-sd" value={s.designation ?? ''} maxLength={150} placeholder="e.g. Emergency Physician" onChange={(e) => setSignature('designation', e.target.value)} />
            </FormField>
            <FormField label="Department" htmlFor="rl-sdep">
              <Input id="rl-sdep" value={s.department ?? ''} maxLength={150} placeholder="e.g. Emergency Medicine" onChange={(e) => setSignature('department', e.target.value)} />
            </FormField>
          </div>

          {editable && aiEnabled && canDraft && (
            <div className="flex flex-col gap-1 border-t border-line pt-2">
              <Button type="button" size="sm" variant="ghost" className="self-start" loading={busy === 'ai'} disabled={!!busy} onClick={writeWithAi}>
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Write with AI
              </Button>
              <span className="text-[11.5px] text-ink-muted">
                Writes the letter text from the ED record and this form
                {aiProfile === undefined ? ", following the hospital's Referral Letter profile (Admin → AI Assistant Role) when one is active."
                  : aiProfile ? <>, following the hospital profile <b className="text-ink">{aiProfile}</b>.</>
                  : <>. No Referral Letter profile is active, so the built-in instructions were used.</>}
              </span>
            </div>
          )}
          {editable && (
            <div className="flex flex-wrap items-center gap-2 border-t border-line pt-2 text-[11.5px] text-ink-muted">
              {bodyFollowsForm
                ? <span>The letter text follows this form until you edit it.</span>
                : <span>You have edited the letter text, so changes here no longer update it.</span>}
              {!bodyFollowsForm && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setConfirm('rebuild')}>
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Rebuild text from the form
                </Button>
              )}
            </div>
          )}
        </fieldset>

        {/* The letter as it prints */}
        <div className="min-w-0 overflow-x-auto rounded-[var(--radius)] border border-line bg-muted p-3">
          <article aria-label="Referral letter preview"
            className="mx-auto w-full max-w-[794px] bg-white px-8 py-8 font-serif text-[15px] leading-relaxed text-neutral-900 shadow-sm sm:px-14">
            <header className="flex items-center gap-3 border-b-2 border-neutral-800 pb-2">
              {branding.logo_url && <img src={branding.logo_url} alt="" className="max-h-16 max-w-[70px]" />}
              <div className={cn('flex-1 text-center', branding.logo_url && 'pr-[82px]')}>
                <div className="text-[22px] font-bold tracking-wide">{(lh.name || 'Hospital').toUpperCase()}</div>
                {(lh.address || lh.phone || lh.website) && (
                  <div className="font-sans text-[11.5px] leading-snug text-neutral-600">
                    {lh.address && <div>{lh.address}</div>}
                    {(lh.phone || lh.website) && <div>{[lh.phone && `Phone: ${lh.phone}`, lh.website].filter(Boolean).join(' · ')}</div>}
                  </div>
                )}
              </div>
            </header>
            {status === 'draft' && (
              <div className="mt-2 border border-amber-300 bg-amber-50 px-2 py-0.5 text-center font-sans text-[11px] text-amber-800">
                DRAFT — not finalized
              </div>
            )}
            <div className="mt-5 font-bold">Date: {fmtLetterDate(form.letter_date)}</div>
            <div className="font-sans text-[11px] text-neutral-500">
              {[`Patient: ${p.display_name}`, `UHID ${p.uhid}`, `ED No ${p.er_number}`, p.visit_code && `Visit ${p.visit_code}`].filter(Boolean).join(' · ')}
            </div>
            <div className="mt-5">
              <div>To</div>
              <div>{r.doctor_name ? [drName(r.doctor_name), r.qualification].filter(Boolean).join(', ') : placeholder('Receiving doctor')}</div>
              {r.designation && <div>{r.designation}</div>}
              <div>{r.hospital || r.city ? [r.hospital, r.city].filter(Boolean).join(', ') : placeholder('Hospital, city')}</div>
            </div>
            <div className="mt-5 font-bold">{form.salutation || 'Respected Sir/Madam,'}</div>
            <textarea aria-label="Letter text" value={form.body} readOnly={ro}
              onChange={(e) => { const v = e.target.value; setForm((f) => (f ? { ...f, body: v } : f)); setProblems([]); }}
              className={cn('mt-2 block min-h-[220px] w-full resize-none border-0 bg-transparent p-0 font-serif text-[15px] leading-relaxed text-neutral-900 [field-sizing:content] focus:outline-none',
                !ro && 'rounded-sm ring-1 ring-transparent hover:ring-neutral-200 focus:ring-teal')} />
            <div className="mt-5">Thanking you,</div>
            <div className="h-12" />
            <div>{s.doctor_name ? [drName(s.doctor_name), s.qualification].filter(Boolean).join(', ') : placeholder('Referring doctor')}</div>
            {s.designation && <div>{s.designation}</div>}
            {s.department && <div>{s.department}</div>}
            <div>{fmtLetterDate(form.letter_date)}</div>
            {lh.footer_text && <div className="mt-8 border-t border-neutral-200 pt-1 font-sans text-[10.5px] text-neutral-500">{lh.footer_text}</div>}
          </article>
          {editable && (
            <div className="mx-auto mt-2 max-w-[794px] text-[11.5px] text-ink-muted">
              Click the letter text to edit it. Leave a blank line between paragraphs. Use <b>Preview</b> to see the exact A4 PDF.
              {missing.length > 0 && <> To finalize, add {missing.join(', ')}.</>}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!previewUrl} onOpenChange={(o) => { if (!o) setPreviewUrl(null); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Referral letter — preview</DialogTitle>
            <DialogDescription>{status === 'draft' ? 'Draft copy — marked DRAFT until it is finalized.' : 'The finalized letter as filed.'}</DialogDescription>
          </DialogHeader>
          {previewUrl && <iframe src={previewUrl} title="Referral letter PDF" className="h-[75vh] w-full rounded border border-line" />}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirm === 'finalize'} onOpenChange={(o) => !o && setConfirm(null)} variant="primary"
        title="Finalize this referral letter?"
        description="The letter is saved as a PDF in the patient's record and can no longer be edited. A later correction is made by amending it, which keeps this version."
        confirmLabel="Finalize letter" onConfirm={() => { setConfirm(null); onFinalize(); }} />
      <ConfirmDialog open={confirm === 'discard'} onOpenChange={(o) => !o && setConfirm(null)}
        title="Discard this draft?" description="The draft letter is deleted. Nothing has been filed in the patient record."
        confirmLabel="Discard draft" onConfirm={() => { setConfirm(null); onDiscard(); }} />
      <ConfirmDialog open={confirm === 'rebuild'} onOpenChange={(o) => !o && setConfirm(null)}
        title="Rebuild the letter text?" description="Your edits to the letter text are replaced by text built from the form and the ED record."
        confirmLabel="Rebuild text" onConfirm={() => { setConfirm(null); setForm((f) => (f ? { ...f, body: compose(f) } : f)); }} />
      <ConfirmDialog open={confirm === 'leave'} onOpenChange={(o) => !o && setConfirm(null)}
        title="Leave without saving?" description="Your changes to this letter have not been saved."
        confirmLabel="Leave without saving" onConfirm={() => { setConfirm(null); onClose(); }} />
      <TextPromptDialog open={amendOpen} title="Amend this referral letter"
        description="A new version opens as a draft with the same content. This version stays on file and is marked superseded once the new one is finalized."
        label="Reason for the amendment" multiline maxLength={500} confirmLabel="Start amendment"
        pending={busy === 'amend'} error={amendError} onConfirm={onAmend} onClose={() => setAmendOpen(false)} />
    </div>
  );
}
